import { TranscriptFetchError } from './TranscriptFetchError';
import { TranscriptParseError } from './TranscriptParseError';

export { TranscriptFetchError, TranscriptParseError };

export type TranscriptCue = {
  startTime: number;
  endTime: number;
  text: string;
};

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

// WebVTT spec (https://www.w3.org/TR/webvtt1/#webvtt-timestamp): optional `HH:` (1+ digits),
// then `MM:SS` with each part 0–59, then `.mmm` (exactly 3 digit milliseconds).
const VTT_TIMESTAMP_RE = /^(?:(\d+):)?([0-5]\d):([0-5]\d)\.(\d{3})$/;

const parseTimestamp = (raw: string): number => {
  const match = raw.trim().match(VTT_TIMESTAMP_RE);
  if (!match) {
    throw new TranscriptParseError(`Invalid timestamp: ${raw}`);
  }
  const [, hoursStr, minutesStr, secondsStr, msStr] = match;
  const hours = hoursStr ? Number(hoursStr) : 0;
  const minutes = Number(minutesStr);
  const seconds = Number(secondsStr);
  const milliseconds = Number(msStr);
  return hours * SECONDS_PER_HOUR
    + minutes * SECONDS_PER_MINUTE
    + seconds
    + milliseconds / MS_PER_SECOND;
};

// Remove WebVTT inline tags; voice tags `<v Speaker>` become "Speaker: " prefix.
const stripTags = (text: string): string => {
  const withSpeaker = text.replace(/<v\b[^>]*?\s+([^>]+)>/g, '$1: ');
  return withSpeaker.replace(/<\/?[^>]+>/g, '');
};

const blockToCue = (block: string, isFirst: boolean): TranscriptCue | null => {
  const trimmed = block.trim();
  if (!trimmed) return null;
  if (isFirst && /^WEBVTT/.test(trimmed)) return null;
  if (/^NOTE(\s|$)/.test(trimmed)) return null;

  const lines = trimmed.split('\n');
  const arrowIdx = lines.findIndex(line => line.includes('-->'));
  if (arrowIdx === -1) return null;

  const [start, endWithSettings] = lines[arrowIdx].split('-->').map(part => part.trim());
  const end = endWithSettings.split(/\s+/)[0];

  const startTime = parseTimestamp(start);
  const endTime = parseTimestamp(end);

  const cueText = stripTags(lines.slice(arrowIdx + 1).filter(Boolean).join('\n')).trim();
  if (!cueText) return null;

  return { startTime, endTime, text: cueText };
};

const BOM = 0xfeff;

// Parse a WebVTT document into cues. Throws TranscriptParseError on a missing WEBVTT
// header or malformed timestamps; silently skips NOTE blocks and non-cue blocks.
export const parseVTT = (text: string): TranscriptCue[] => {
  if (!text) throw new TranscriptParseError('Empty VTT content');

  const withoutBom = text.charCodeAt(0) === BOM ? text.slice(1) : text;
  const normalized = withoutBom.replace(/\r\n?/g, '\n');

  if (!/^WEBVTT(\s|$)/.test(normalized)) {
    throw new TranscriptParseError('Missing WEBVTT header');
  }

  return normalized
    .split(/\n{2,}/)
    .map((block, idx) => blockToCue(block, idx === 0))
    .filter((cue): cue is TranscriptCue => cue !== null);
};

export const fetchTranscript = async ({
  muxPlaybackId,
  captionId,
}: {
  muxPlaybackId: string;
  captionId: string;
}): Promise<TranscriptCue[]> => {
  const url = `https://stream.mux.com/${encodeURIComponent(muxPlaybackId)}/text/${encodeURIComponent(captionId)}.vtt`;

  let response: Response;
  try {
    response = await fetch(url);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown error';
    throw new TranscriptFetchError(`Network error fetching VTT: ${message}`);
  }

  if (!response.ok) {
    throw new TranscriptFetchError(
      `Failed to fetch VTT: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  const body = await response.text();
  return parseVTT(body);
};


export type AiTranscriptWord = {
  text: string;
  start_time: number;
  end_time: number;
};

export type AiTranscriptBlock = {
  speaker?: string | null;
  start_time: number;
  end_time: number;
  words?: AiTranscriptWord[];
};

export type AiTranscriptJson = {
  name?: string | null;
  transcript?: AiTranscriptBlock[];
  quality_score?: number | null;
};

// The AI transcript becomes the default source once a completed build exposes its file.
export const getAiTranscriptUrl = (
  video?: { aiTranscript?: { status?: string; jsonFileUrl?: string } },
  dynamicVideoProps?: { prospectTranscript?: { status?: string; jsonFileUrl?: string } }
): string | undefined => {
  const aiTranscript = dynamicVideoProps ? dynamicVideoProps.prospectTranscript : video?.aiTranscript;
  return (aiTranscript?.status === 'completed' && aiTranscript.jsonFileUrl) || undefined;
};

// Sentence-ending punctuation, allowing a trailing quote/bracket.
const SENTENCE_END_RE = /[.!?…]["')\]]?$/;
// A silence this long between words reads as a natural break.
const PAUSE_BREAK_S = 0.8;
// Caps for run-on speech with no punctuation or pauses.
const MAX_CUE_DURATION_S = 7;
const MAX_CUE_WORDS = 24;

const isPhraseLevel = (words: AiTranscriptWord[]): boolean => {
  if (!words.length) return false;
  const totalTokens = words.reduce(
    (sum, word) => sum + (word.text || '').trim().split(/\s+/).filter(Boolean).length,
    0,
  );
  return totalTokens / words.length >= 3;
};

const entryToCue = (word: AiTranscriptWord): TranscriptCue => ({
  startTime: word.start_time ?? 0,
  endTime: word.end_time ?? 0,
  text: (word.text || '').replace(/\s+/g, ' ').trim(),
});

// Word-level entries would collapse the panel to a single 0:00 line if taken as-is.
// Rebuild natural cues from the word timestamps instead: break on sentence ends,
// long pauses, or when a cue grows past a comfortable reading length.
const wordsToCues = (words: AiTranscriptWord[]): TranscriptCue[] => {
  if (isPhraseLevel(words)) {
    return words.map(entryToCue).filter((cue) => cue.text);
  }
  const cues: TranscriptCue[] = [];
  let current: AiTranscriptWord[] = [];

  const flush = () => {
    if (!current.length) return;
    // Each word already carries a trailing space; collapse just in case.
    const text = current.map((word) => word.text || '').join('').replace(/\s+/g, ' ').trim();
    if (text) {
      cues.push({
        startTime: current[0].start_time ?? 0,
        endTime: current[current.length - 1].end_time ?? 0,
        text,
      });
    }
    current = [];
  };

  words.forEach((word, index) => {
    current.push(word);
    const next = words[index + 1];
    const duration = (word.end_time ?? 0) - (current[0].start_time ?? 0);
    const pause = next ? (next.start_time ?? 0) - (word.end_time ?? 0) : 0;
    if (
      SENTENCE_END_RE.test((word.text || '').trim())
      || pause >= PAUSE_BREAK_S
      || duration >= MAX_CUE_DURATION_S
      || current.length >= MAX_CUE_WORDS
    ) {
      flush();
    }
  });
  flush();

  return cues;
};

// Map the AI transcript onto the same cue shape the VTT parser produces, so the
// panel is transcript-source agnostic. Throws on an empty/foreign shape so callers can
// fall back to the standard Mux transcript.
export const aiTranscriptToCues = (json: AiTranscriptJson): TranscriptCue[] => {
  if (!json || !Array.isArray(json.transcript)) {
    throw new TranscriptParseError('AI transcript JSON has no transcript blocks');
  }
  const cues = json.transcript.reduce<TranscriptCue[]>(
    (acc, block) => acc.concat(wordsToCues(block.words || [])),
    [],
  );

  if (!cues.length) {
    throw new TranscriptParseError('AI transcript JSON has no spoken content');
  }
  return cues;
};

// Fetch and parse the AI transcript JSON from its CDN/GCS URL; throws on network or parse failure.
export const fetchAiTranscript = async (jsonFileUrl: string): Promise<TranscriptCue[]> => {
  let response: Response;
  try {
    response = await fetch(jsonFileUrl);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown error';
    throw new TranscriptFetchError(`Network error fetching AI transcript: ${message}`);
  }

  if (!response.ok) {
    throw new TranscriptFetchError(
      `Failed to fetch AI transcript: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  let json: AiTranscriptJson;
  try {
    json = (await response.json()) as AiTranscriptJson;
  } catch {
    throw new TranscriptParseError('AI transcript is not valid JSON');
  }
  return aiTranscriptToCues(json);
};

// Linear scan: find the cue active at `time`. Transcripts are small (~hundreds of cues max)
// so this is fine — premature optimization isn't warranted. Returns -1 if no cue covers the time.
export const findActiveCueIndex = (cues: TranscriptCue[], time: number): number => {
  for (let i = 0; i < cues.length; i += 1) {
    const cue = cues[i];
    if (cue.startTime <= time && cue.endTime > time) return i;
  }
  return -1;
};
